import { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, Calculator, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { v2ListComponents, v2ListDerivedVariables, v2ListStatutoryConfigs } from "../SalaryApiV2";
import { entSimulateFormula } from "../salaryEnterpriseApi";
import { DEFAULT_PF_WAGE_CEILING } from "../../../Statutary/statutoryCalculators";

/** Mirrors the PF engine: a stored wage_limit only applies when above the statutory ceiling. */
const effectivePfCap = (s) => Math.max(Number(s.wage_limit) || 0, DEFAULT_PF_WAGE_CEILING);

const CATEGORY_ORDER = ["earning", "benefit", "deduction", "statutory", "employer_contribution"];
const CATEGORY_LABELS = {
  earning: "Earnings",
  benefit: "Benefits (pre-tax)",
  deduction: "Deductions",
  statutory: "Statutory",
  employer_contribution: "Employer contributions",
};

const SYSTEM_RULES = {
  PF: (cfg) => {
    const s = cfg?.settings || cfg || {};
    const cap = effectivePfCap(s);
    const empRate = s.employee_rate ?? 12;
    const capped = s.is_capped !== false;
    return capped
      ? `Employee PF = min(PF_WAGE, ₹${cap}) × ${empRate}%  |  PF_WAGE from context key "${s.pf_wage_context_key || "PF_WAGE"}"`
      : `Employee PF = PF_WAGE × ${empRate}%  (uncapped)`;
  },
  ESI: (cfg) => {
    const s = cfg?.settings || cfg || {};
    const limit = s.wage_limit ?? s.gross_limit ?? 21000;
    return `ESI applies when gross ≤ ₹${limit}. Employee 0.75%, Employer 3.25% of gross.`;
  },
  PT: (cfg) => {
    const s = cfg?.settings || cfg || {};
    const state = s.state_code || "state-specific";
    return `Professional Tax based on gross salary slabs for state ${state}.`;
  },
  TDS: () =>
    "Monthly TDS = annual tax on taxable income ÷ 12. Slabs depend on tax regime (new/old) configured in payroll.",
};

function defaultSampleContext() {
  const ctc = 1200000;
  const monthly = Math.round(ctc / 12);
  const basic = Math.round(monthly * 0.4);
  const hra = Math.round(basic * 0.5);
  const gross = basic + hra + Math.round(monthly * 0.2);
  return {
    CTC: ctc,
    BASIC: basic,
    HRA: hra,
    GROSS: gross,
    PF_WAGE: basic,
    WORKING_DAYS: 26,
    PAID_DAYS: 26,
    LOP_DAYS: 0,
    OT_HOURS: 0,
  };
}

function categoryBadgeClass(category) {
  if (category === "deduction") return "bw-badge-deduction";
  if (category === "benefit") return "bw-badge-benefit";
  if (category === "employer_contribution" || category === "statutory") return "bw-badge-neutral";
  return "bw-badge-earning";
}

function formatCurrency(val) {
  if (val == null || Number.isNaN(Number(val))) return "—";
  return `₹${Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function computePfSample(ctx, cfg) {
  const s = cfg?.settings || cfg || {};
  const wageCap = effectivePfCap(s);
  const empRate = Number(s.employee_rate ?? 12);
  const capped = s.is_capped !== false;
  const pfWageKey = s.pf_wage_context_key || "PF_WAGE";
  const pfWage = Number(ctx[pfWageKey] ?? ctx.BASIC ?? 0);
  const effective = capped ? Math.min(pfWage, wageCap) : pfWage;
  const empPf = Math.round((effective * empRate) / 100);
  return { pfWage: effective, employeePf: empPf };
}

function ComponentCard({ comp, sampleContext, statutoryByCode, formulaResults }) {
  const [open, setOpen] = useState(false);
  const displayCat = comp.display_category || comp.component_category;
  const calcType = comp.calculation_type;
  const code = (comp.code || "").toUpperCase();
  const sysCode = (comp.system_code || "").toUpperCase();

  let ruleText = null;
  let sampleResult = null;

  if (calcType === "formula" && comp.formula_expression) {
    ruleText = comp.formula_expression;
    sampleResult = formulaResults[code];
  } else if (calcType === "system" && sysCode) {
    const cfg = statutoryByCode[sysCode];
    const builder = SYSTEM_RULES[sysCode];
    ruleText = builder ? builder(cfg) : `Resolved by statutory engine: ${sysCode}`;
    if (sysCode === "PF") {
      const pf = computePfSample(sampleContext, cfg);
      sampleResult = pf.employeePf;
    }
  } else if (calcType === "fixed") {
    ruleText = "Fixed amount — set per employee in salary structure or template.";
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="font-semibold text-slate-900">{code}</span>
          <span className="text-sm text-slate-600 truncate">{comp.name}</span>
          <span className={`bw-badge ${categoryBadgeClass(displayCat)}`}>{displayCat}</span>
          <span className="bw-badge bw-badge-neutral">{calcType}</span>
        </div>
        {open ? <ChevronUp size={16} className="shrink-0 text-slate-400" /> : <ChevronDown size={16} className="shrink-0 text-slate-400" />}
      </button>

      {open ? (
        <div className="px-4 pb-4 border-t border-slate-100 space-y-3 text-sm">
          {comp.description ? <p className="text-slate-600 pt-3">{comp.description}</p> : null}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Formula / rule (live)</p>
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-x-auto font-mono text-slate-800 whitespace-pre-wrap">
              {ruleText || "—"}
            </pre>
          </div>

          {calcType === "formula" && comp.formula_expression ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Sample result</p>
              <p className="font-mono text-teal-800 font-semibold">{formatCurrency(sampleResult)}</p>
            </div>
          ) : null}

          {calcType === "system" && sysCode === "PF" ? (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Sample PF (employee)</p>
              <p className="font-mono text-teal-800 font-semibold">{formatCurrency(sampleResult)}</p>
            </div>
          ) : null}

          {comp.meta?.attendance_proration_mode ? (
            <p className="text-xs text-slate-500">
              Attendance proration: <strong>{comp.meta.attendance_proration_mode}</strong>
            </p>
          ) : null}

          {comp.rounding_rule?.scale != null ? (
            <p className="text-xs text-slate-500">
              Rounding: {comp.rounding_rule.scale} decimal places
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function SalaryComponentUserGuide() {
  const [components, setComponents] = useState([]);
  const [derived, setDerived] = useState([]);
  const [statutory, setStatutory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sampleContext, setSampleContext] = useState(defaultSampleContext);
  const [formulaResults, setFormulaResults] = useState({});
  const [simulating, setSimulating] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [comps, dvs, stats] = await Promise.all([
        v2ListComponents({ active: true }),
        v2ListDerivedVariables(),
        v2ListStatutoryConfigs(),
      ]);
      setComponents(Array.isArray(comps) ? comps : []);
      setDerived(Array.isArray(dvs) ? dvs : []);
      setStatutory(Array.isArray(stats) ? stats : []);
    } catch (e) {
      setError(e?.detail || e?.message || "Failed to load component guide data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const statutoryByCode = useMemo(() => {
    const map = {};
    for (const row of statutory) {
      const code = (row.statutory_code || "").toUpperCase();
      if (!map[code] || String(row.effective_from || "") > String(map[code].effective_from || "")) {
        map[code] = row;
      }
    }
    return map;
  }, [statutory]);

  const mappedComponents = useMemo(
    () =>
      components
        .map((r) => ({
          ...r,
          display_category: r.meta?.payroll_display_category || r.component_category,
        }))
        .sort((a, b) => (a.code || "").localeCompare(b.code || "")),
    [components]
  );

  const grouped = useMemo(() => {
    const g = {};
    for (const cat of CATEGORY_ORDER) g[cat] = [];
    for (const c of mappedComponents) {
      const cat = c.display_category || "earning";
      if (!g[cat]) g[cat] = [];
      g[cat].push(c);
    }
    return g;
  }, [mappedComponents]);

  const runSimulations = useCallback(async () => {
    const formulaComps = mappedComponents.filter(
      (c) => c.calculation_type === "formula" && c.formula_expression?.trim()
    );
    if (formulaComps.length === 0) {
      setFormulaResults({});
      return;
    }
    setSimulating(true);
    const ctx = { ...sampleContext };
    const results = {};
    try {
      for (const comp of formulaComps) {
        const code = (comp.code || "").toUpperCase();
        try {
          const res = await entSimulateFormula({ expression: comp.formula_expression.trim(), sample_context: ctx });
          const val = res?.result ?? 0;
          results[code] = val;
          ctx[code] = val;
        } catch {
          results[code] = null;
        }
      }
      setFormulaResults(results);
    } finally {
      setSimulating(false);
    }
  }, [mappedComponents, sampleContext]);

  useEffect(() => {
    const t = setTimeout(() => {
      runSimulations();
    }, 400);
    return () => clearTimeout(t);
  }, [runSimulations]);

  function updateSample(key, raw) {
    const num = Number(raw);
    setSampleContext((p) => ({ ...p, [key]: Number.isFinite(num) ? num : 0 }));
  }

  const visibleCategories =
    activeCategory === "all" ? CATEGORY_ORDER.filter((c) => (grouped[c] || []).length > 0) : [activeCategory];

  return (
    <div className="bw-stack">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="bw-section-title flex items-center gap-2">
            <BookOpen size={20} className="text-teal-700" />
            Component calculation guide
          </h2>
          <p className="bw-section-hint">
            Live formulas and statutory rules from your organisation&apos;s current salary engine configuration.
          </p>
        </div>
        <button type="button" className="bw-btn bw-btn-secondary" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <div className="bw-alert-error">
          <span>{error}</span>
          <button type="button" className="bw-btn bw-btn-secondary" onClick={load}>
            Retry
          </button>
        </div>
      ) : null}

      {/* Sample context */}
      <div className="rounded-xl border border-teal-200 bg-teal-50/50 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <Calculator size={18} className="text-teal-700" />
          <p className="font-semibold text-slate-900">Sample payroll context</p>
          {simulating ? <span className="text-xs text-slate-500">Recalculating…</span> : null}
        </div>
        <p className="text-xs text-slate-600 mb-3">
          Adjust values below to see live formula results for each component. Formulas evaluate in component order using this context.
        </p>
        <div className="bw-form-grid">
          {["CTC", "BASIC", "HRA", "GROSS", "PF_WAGE", "WORKING_DAYS", "PAID_DAYS"].map((key) => (
            <div key={key} className="bw-field xl-3">
              <label className="bw-label text-xs">{key}</label>
              <input
                type="number"
                className="bw-input"
                value={sampleContext[key] ?? 0}
                onChange={(e) => updateSample(key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={`bw-btn ${activeCategory === "all" ? "bw-btn-primary" : "bw-btn-secondary"}`}
          onClick={() => setActiveCategory("all")}
        >
          All
        </button>
        {CATEGORY_ORDER.map((cat) =>
          (grouped[cat] || []).length > 0 ? (
            <button
              key={cat}
              type="button"
              className={`bw-btn ${activeCategory === cat ? "bw-btn-primary" : "bw-btn-secondary"}`}
              onClick={() => setActiveCategory(cat)}
            >
              {CATEGORY_LABELS[cat]} ({grouped[cat].length})
            </button>
          ) : null
        )}
      </div>

      {/* Components by category */}
      {loading ? (
        <p className="text-sm text-slate-500">Loading components…</p>
      ) : mappedComponents.length === 0 ? (
        <div className="bw-alert-error">
          No components configured. Add components under the Components tab or load the system catalog.
        </div>
      ) : (
        visibleCategories.map((cat) =>
          (grouped[cat] || []).length > 0 ? (
            <section key={cat}>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">{CATEGORY_LABELS[cat] || cat}</h3>
              <div className="space-y-2">
                {grouped[cat].map((comp) => (
                  <ComponentCard
                    key={comp.component_id}
                    comp={comp}
                    sampleContext={sampleContext}
                    statutoryByCode={statutoryByCode}
                    formulaResults={formulaResults}
                  />
                ))}
              </div>
            </section>
          ) : null
        )
      )}

      {/* Derived variables */}
      {derived.length > 0 ? (
        <section>
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Derived variables (formulas)</h3>
          <div className="bw-table-wrap">
            <table className="bw-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Expression</th>
                </tr>
              </thead>
              <tbody>
                {derived.map((d) => (
                  <tr key={d.variable_id || d.code}>
                    <td style={{ fontWeight: 600 }}>{d.code}</td>
                    <td>{d.name}</td>
                    <td>
                      <code className="text-xs bg-slate-50 px-2 py-1 rounded">{d.expression}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {/* Statutory configs */}
      {statutory.length > 0 ? (
        <section>
          <h3 className="text-sm font-semibold text-slate-800 mb-2">Active statutory rules</h3>
          <div className="space-y-2">
            {Object.entries(statutoryByCode).map(([code, cfg]) => (
              <div key={code} className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-semibold text-slate-900">{code}</span>
                  <span className="text-xs text-slate-500">Effective from {cfg.effective_from || "—"}</span>
                  {cfg.is_enabled === false ? (
                    <span className="bw-badge bw-badge-deduction">Disabled</span>
                  ) : (
                    <span className="bw-badge bw-badge-earning">Enabled</span>
                  )}
                </div>
                <p className="text-slate-700 font-mono text-xs">
                  {SYSTEM_RULES[code] ? SYSTEM_RULES[code](cfg) : JSON.stringify(cfg.settings || {}, null, 0)}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
